import React, { useContext, useEffect, useState } from 'react';
import {
    Tag,
    Tooltip,
    TablePaginationConfig,
} from 'antd';
import { useLocation } from 'react-router-dom';
import { ColumnsType } from 'antd/lib/table/Table';
import { checkAgencyLogin, getCurrentTerm } from 'constants/GlobalConfig';
import { useAppSelector } from 'flex/utils';
import { BaseTable } from 'components/modules/BaseTable';
import { ScoringStatusTag } from './common/ScoringStatusTag';
import 'styles/modules/test_status_modal.scss';
import { useAppCategoryResult } from '../utils/useAppCategoryResult';
import { StatusSearch } from './StatusSearch';
import { BreadCrumbContext } from './index';
import { selectors } from 'flex/Selectors';
import { findApplicationAgs, findTakeHasNewScoreLineItem } from '../utils/Helper';


type DataSource = App.AppStatus.UsersListItem;
type FormValuesKeys = keyof Required<Omit<Http.Connection.Request.Parameter.App.GroupStatusSearch, 'only_ags_application'>>;
type FormValues = Record<FormValuesKeys, string | undefined>;

interface Props {
    category: App.AppsCategory
    categoryResult: ReturnType<typeof useAppCategoryResult>[0],
    onSearch: ReturnType<typeof useAppCategoryResult>[1],
    resetCategoryResult: ReturnType<typeof useAppCategoryResult>[2],
    visible: boolean,
}

const INIT_SEARCH: FormValues = {
    role_uuid: undefined,
    school_class_uuid: undefined,
    term_uuid: undefined,
};

export const StudentList: React.FC<Props> = ({
    category,
    categoryResult,
    onSearch,
    resetCategoryResult,
    visible,
}) => {
    const { setBreadcrumb } = useContext(BreadCrumbContext);
    const [search, setSearch] = useState<FormValues>(INIT_SEARCH);
    const [pagination, setPagination] = useState<TablePaginationConfig>({
        ...categoryResult.pagination,
        showTotal: (total, range) => {
            return (`全${total}件中 ${range[0]}-${range[1]} 件`)
        },
        onChange: (page, pageSize) => {
            setPagination(pagination => ({
                ...pagination,
                current: page,
                pageSize,
            }));
            onSearch(category.uuid, search, page, pageSize);
        },
    });
    const { termList } = useAppSelector(selectors.globalState);
    const isControllableUser = useAppSelector(state => state.isControllableUser.payload);
    const categoryApplications: any[] = findApplicationAgs(category?.applications);

    // URLからUUIDを取得するための処理
    const user = useAppSelector(selectors.user);
    const isAgencyLogin = checkAgencyLogin(user);
    const useQuery = () => new URLSearchParams(useLocation().search);
    const query = useQuery();

    useEffect(() => {
        if (!visible) return;
        const newSearch = {
            ...INIT_SEARCH,
            term_uuid: getCurrentTerm(termList).uuid ?? '',
        };
        resetCategoryResult();
        setSearch(newSearch);
        onSearch(isAgencyLogin ? query.get('ag') ?? '' : category?.uuid, newSearch, 1, pagination.pageSize ?? 50);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    useEffect(() => {
        setPagination(pagination => ({
            ...pagination,
            total: categoryResult.pagination.total,
        }));
    }, [categoryResult.pagination]);

    useEffect(() => {
        setPagination(pagination => ({
            ...pagination,
            onChange: (page, pageSize) => {
                setPagination(pagination => ({
                    ...pagination,
                    current: page,
                    pageSize,
                }));
                onSearch(category.uuid, search, page, pageSize);
            },
        }));
        // category?.uuid, searchの監視で正常に動作しているため
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category?.uuid, search]);

    const handleClickUserName = (userName: string, uuid: string) => {
        setBreadcrumb({ name: userName, uuid: uuid, type: 'Student' });
    };

    const handleClickTestName = (title: string, uuid: string) => {
        setBreadcrumb({ name: title, uuid: uuid, type: 'Application' });
    };

    /**
     * lineItems に従ってテーブルのタイトルと本文の表示を処理します。
     *
     * @param {App.ForAdmin | App.ForManager} application
     * @param {number} index
     * @returns
     */
    const generateLineItems = (application: App.ForAdmin | App.ForManager, index: number) => {
        // アプリ追加後
        if (!application?.line_items) {
            return [{ title: '', key: 0, width: 250, render: () => <></> }];
        }

        // Line Item作成後（スコア送信前）
        return application.line_items.map((lineItem, lIndex) => {
            const title = lineItem.label;
            if (application.takes?.length === 0) return { key: lIndex, title: title, render: () => <div>―</div> };

            // スコア送信後
            return {
                key: lIndex,
                title: title,
                width: 250,
                render: (_: any, rowRecord: App.AppStatus.UsersListItem) => {
                    const take = findTakeHasNewScoreLineItem(rowRecord?.applications?.[index]);
                    const lineItem = take?.line_items?.[lIndex];
                    const gradingProgress = lineItem?.score_line_items?.[0]?.grading_progress ?? undefined;

                    return (
                        <div>
                            <span style={{ marginRight: '1rem' }}>{createTextOfLatestApplicationResult(lineItem)}</span>
                            <ScoringStatusTag status={gradingProgress} />
                        </div>
                    );
                },
            };
        });
    };

    const generateChildren = (): ColumnsType<DataSource> => {
        // アプリ追加前
        if (categoryApplications.length === 0) return [{ title: '', key: 0, width: 250, render: () => <></> }];

        // アプリ追加後
        return categoryApplications.map((application, index) => ({
            title: (
                <Tooltip title={application.title} >
                    <button
                        className='test-result-button text-button'
                        onClick={() => handleClickTestName(application.title, application.uuid)}
                        style={{ marginRight: '0.5rem' }}
                    >
                        {application.title}
                    </button>
                </Tooltip>
            ),
            key: index,
            width: 250,
            className: 'test-result-column',
            children: generateLineItems(application, index),
        }));
    };
    const dataSource = categoryResult.usersList;

    const columns: ColumnsType<DataSource> = [
        {
            title: '氏名',
            width: 200,
            fixed: 'left',
            key: 'name',
            render: (_, record) => {
                return (
                    <Tooltip
                        mouseEnterDelay={1}
                        title='生徒ごとのビューを開く'
                    >
                        <button
                            className='text-button'
                            onClick={
                                () => {
                                    handleClickUserName(record.name, record.uuid);
                                }
                            }
                        >
                            {record.name}
                        </button>
                    </Tooltip>
                );
            }
        },
        {
            title: '配信対象者',
            children: [
                {
                    title: 'クラス名',
                    width: 200,
                    dataIndex: 'belongs',
                    key: 'class',
                    render: (belongs: DataSource['belongs']) => (
                        belongs.map((val, index) => (
                            <div key={`${val.school_class.name}-${index}`}>
                                <Tag color='#AAAAAA' style={{
                                    width: '75px',
                                    textAlign: 'center',
                                    borderRadius: '5px',
                                    marginRight: '0.5rem'
                                }}>
                                    {val.school_class.term.name}
                                </Tag>
                                {val.school_class.name}
                            </div>
                        ))
                    )
                },
                {
                    title: '出席番号',
                    width: 150,
                    dataIndex: 'belongs',
                    key: 'roles',
                    render: (belongs: DataSource['belongs']) => (
                        belongs.map((val, index) => (
                            <div key={index}>{val.number}</div>
                        ))
                    )
                },
                {
                    title: '学習の進捗',
                    width: 200,
                    dataIndex: 'applications',
                    key: 'progress',
                    render: (applications: DataSource['applications']) => {
                        const totalExams = applications.length;
                        const finishedExams =
                            applications.reduce((prev, app) => {
                                const take = findTakeHasNewScoreLineItem(app);
                                return take ? ++prev : prev;
                        }, 0) || 0;
                        return <div>{totalExams ? `${finishedExams} / ${totalExams} 学習完了` : '- / -'}</div>;
                    }
                }
            ]
        },
        {
            title: <div style={{ position: 'sticky', left: '200px' }}>実施結果</div>,
            align: 'center',
            width: 250 * categoryApplications.length,
            children: generateChildren()
        },
    ];

    const calculateScrollWidth = () => {
        const lineItemLength =
            categoryApplications.reduce((prev, app) => {
                return prev + app.line_items.length;
            }, 0) || 0;
        return 550 + (200 * lineItemLength);
    };

    const handleSearch = (values: FormValues) => {
        onSearch(category.uuid, values, 1, pagination.pageSize ?? 50);
        setSearch(values);
        setPagination(pagination => ({
            ...pagination,
            current: 1,
        }));
    };

    return (
        <div>
            <StatusSearch
                values={search}
                onSearch={handleSearch}
                loading={categoryResult.isLoading}
                visible={visible}
            />

            <BaseTable
                loading={categoryResult.isLoading}
                columns={columns}
                dataSource={dataSource}
                emptyDescription='対象の学習結果が存在しません'
                size='middle'
                pagination={pagination}
                scroll={{ x: calculateScrollWidth() }}
                className='all-white-table'
                sticky={{ getContainer: () => {
                    return document.querySelector('.ant-modal-wrap') as HTMLElement;
                }}}
            />
        </div>
    );
};

const createTextOfLatestApplicationResult = (lineItem: App.ApplicationLineItem) => {
    const score_line_items = lineItem?.score_line_items && lineItem.score_line_items[0];
    return `${score_line_items?.score_given ?? '-'} / ${score_line_items?.score_maximum ?? '-'}`
};